import { createSlice, isAsyncThunkAction } from '@reduxjs/toolkit';

import { PLAN_TYPE } from '@/const/plans';

import { handleAsyncThunkActionState, initialAsyncState } from '../utils';
import { STEPS } from './constants';
import * as thunks from './thunks';

export { STEPS };

const initialState = {
	step: STEPS.BILLING,
	currentPlan: PLAN_TYPE.TRIAL,
	fetchPrices: initialAsyncState,
	isFastCheckoutModalOpen: false,
	isPaymentBreakdownModalOpen: false,
	priceId: null,
	planName: '',
	prices: [],
	initializing: false,
	vatVisible: false,
	clientSecret: null,
	userCountry: null,
	fixedErrorCode: null,
	initCheckout: initialAsyncState,
	getPrices: initialAsyncState,
	plansModalOpen: false,
	selectedPlan: null,
	fromBilling: false,
	cannotProcessRetry: false,
	paymentHandlerId: null
};

const checkoutSlice = createSlice(
	{
		name: 'checkout',
		initialState,
		reducers: {
			setIsFastCheckoutModalOpen(state, action) {
				state.isFastCheckoutModalOpen = action.payload;
			},
			setIsPaymentBreakdownModalOpen(state, action) {
				state.isPaymentBreakdownModalOpen = action.payload;
			},
			setPlan(state, action) {
				state.priceId = action.payload;
				thunks.saveCheckoutData(state);
			},
			setStep(state, action) {
				state.step = action.payload;
				thunks.saveCheckoutData(state);
			},
			setPrice(state, action) {
				const { id, clientSecret, paymentHandlerId } = action.payload;
				state.priceId = id || null;
				state.clientSecret = clientSecret || null;
				state.paymentHandlerId = paymentHandlerId || null;
				thunks.saveCheckoutData(state);
			},
			setPlansModalOpen(state, action) {
				state.plansModalOpen = action.payload;
			},
			setSelectedPlan(state, action) {
				state.selectedPlan = action.payload;
			},
			setFromBilling(state, action) {
				state.fromBilling = action.payload;
			},
			clearData(state) {
				Object.assign(state, initialState);
			}
		},
		extraReducers(builder) {
			builder.addCase(thunks.fetchPrices.fulfilled, (state, action) => {
				state.prices = action.payload.prices.filter(
					p =>
						p.active &&
						![PLAN_TYPE.SERVICE, PLAN_TYPE.STORAGE].includes(p.name)
				);
				state.pricesReady = true;
			});

			builder.addCase(thunks.getPrices.fulfilled, (state, action) => {
				const { userCountry, prices } = action.payload;

				if (userCountry) {
					state.userCountry = userCountry;
				}

				state.prices = prices;
			});

			builder.addCase(thunks.initCheckout.fulfilled, (state, action) => {
				const {
					priceId,
					userCountry,
					activePrices,
					step,
					paymentHandlerId,
					clientSecret
				} = action.payload;

				if (userCountry) {
					state.userCountry = userCountry;
				}

				if (priceId) {
					state.priceId = priceId;
				}

				if (paymentHandlerId) {
					state.paymentHandlerId = paymentHandlerId;
				}

				state.prices = activePrices;
				state.step = step;

				state.clientSecret = clientSecret || null;

				state.cannotProcessRetry = paymentHandlerId && !clientSecret;

				thunks.saveCheckoutData(state);
			});

			builder.addMatcher(
				action =>
					isAsyncThunkAction(action) &&
					[
						thunks.getPrices.typePrefix,
						thunks.initCheckout.typePrefix
					].some(prefix => action.type.startsWith(prefix)),
				handleAsyncThunkActionState({
					ignoreData: true
				})
			);
		}
	},
	initialState
);

export const {
	setIsFastCheckoutModalOpen,
	setIsPaymentBreakdownModalOpen,
	setStep,
	setPlan,
	setPrice,
	setPlansModalOpen,
	setSelectedPlan,
	clearData,
	setFromBilling
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
